import type { ILocalTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-sdk-ng';

import { StudioLogger } from '@studio/logger/StudioLogger';
import { setupAudioDeviceListeners } from '../events/setupDeviceListeners';

const LOG = new StudioLogger('agoraAudioLogger');

/* eslint-disable no-unused-vars */
interface Actions {
  showNotification: (...args: any[]) => any;
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

/**
 * Agora SDK Audio Helper Functions
 * These functions provide a consistent interface for Agora audio/video operations
 */

/**
 * Sets the mute status of the local user's audio stream
 * @param localAudioTrack - The Agora local audio track instance
 * @param isMuted - Whether to mute (true) or unmute (false) the audio
 * @returns Promise<void>
 */
export async function muteLocalAudioStream(
  localAudioTrack: IMicrophoneAudioTrack,
  isMuted: boolean,
  client: IAgoraRTCClient,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions
): Promise<void> {
  if (!localAudioTrack) {
    try {
      const microphones = await AgoraRTC.getMicrophones();
      if (microphones.length === 0) throw new Error('No microphone found');
      const microphoneId = microphones[0]?.deviceId;
      const newAudioTrack: IMicrophoneAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId,
        ANS: true,
        AEC: true
      });

      setupAudioDeviceListeners(newAudioTrack, dispatch, deviceActions);
      await client.publish(newAudioTrack);
      LOG.info(`Successfully created new audio track for device: ${microphoneId}`);
    } catch (error) {
      LOG.error('Failed to create new audio track when toggling audio:', error);
    }
  }

  try {
    await localAudioTrack.setMuted(isMuted);
    LOG.info(`Agora Local audio ${isMuted ? 'muted' : 'unmuted'}`);
  } catch (error) {
    LOG.error(`Failed to ${isMuted ? 'mute' : 'unmute'} local audio stream:`, error);
    throw error;
  }
}

/**
 * Switches the audio input device for the local audio track
 * @param client - The Agora RTC client instance
 * @param deviceId - The new audio input device ID
 * @returns Promise<IMicrophoneAudioTrack> - The audio track with the switched device
 */
export async function switchAudioInput(
  client: IAgoraRTCClient,
  deviceId: string,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions
): Promise<IMicrophoneAudioTrack | null> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    const currentAudioTrack = client.localTracks.find(
      (track: ILocalTrack) => track.trackMediaType === 'audio'
    ) as IMicrophoneAudioTrack | null;

    if (!currentAudioTrack) {
      try {
        const newAudioTrack: IMicrophoneAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: deviceId,
          ANS: true,
          AEC: true
        });

        setupAudioDeviceListeners(newAudioTrack, dispatch, deviceActions);
        await client.publish(newAudioTrack);
        LOG.info(`Successfully created new audio track for device: ${deviceId}`);
        return newAudioTrack;
      } catch (error) {
        LOG.error('Failed to create new audio track when switching audio input:', error);
        return null;
      }
    }

    await currentAudioTrack.setDevice(deviceId);
    LOG.info(`Successfully switched audio input device to: ${deviceId}`);
    return currentAudioTrack;
  } catch (error) {
    LOG.error(`Failed to switch audio input device to ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Lists all available audio input devices
 * @returns Promise<MediaDeviceInfo[]> - Array of available audio input devices
 */
export async function listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await AgoraRTC.getDevices();
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

    return audioInputDevices;
  } catch (error) {
    LOG.error('Failed to list audio input devices:', error);
    throw error;
  }
}
